import React, { useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, NavLink, Link } from "react-router-dom";
import {
  UserCircle2,
  Briefcase,
  Images,
  BadgeCheck,
  ExternalLink,
  Camera,
  Loader2,
  Store,
} from "lucide-react";
import { ProfileProvider, useProfile } from "../../context/ProfileContext";
import AuthedImage from "../../components/AuthedImage";
import { humanizeAxiosError } from "../../lib/api";
import {
  ProfileProgressRing,
  Breadcrumb,
  Skeleton,
} from "../../design-system";

/**
 * MiPerfilLayout — layout del módulo Mi Perfil con sub-nav lateral (desktop)
 * y segmentos horizontales (mobile). Header con avatar + progreso.
 * Alineado con Etapa 3 §§4.1 y 7.
 */

// Tipos de usuario que operan profesionalmente y ven Perfil Profesional + Portafolio.
const TIPOS_PROFESIONALES = new Set(["maestro", "contratista", "profesional", "ferreteria"]);

const SECCIONES = [
  { to: "/mi-perfil/global", label: "Perfil Global", icon: UserCircle2, testid: "mp-nav-global" },
  { to: "/mi-perfil/profesional", label: "Perfil Profesional", icon: Briefcase, testid: "mp-nav-profesional", soloProfesional: true },
  { to: "/mi-perfil/portafolio", label: "Portafolio", icon: Images, testid: "mp-nav-portafolio", soloProfesional: true },
  { to: "/mi-perfil/verificaciones", label: "Verificaciones", icon: BadgeCheck, testid: "mp-nav-verificaciones" },
];

function seccionesVisibles(tipoUsuario) {
  const esProfesional = TIPOS_PROFESIONALES.has(tipoUsuario);
  return SECCIONES.filter((s) => !s.soloProfesional || esProfesional);
}

function AvatarUploader({ perfilGlobal, initials }) {
  const { uploadAvatar } = useProfile();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Formato no válido (JPG, PNG o WEBP)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("La imagen supera 5 MB");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      await uploadAvatar(file);
    } catch (err) {
      setError(humanizeAxiosError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex-shrink-0">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="group relative w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border border-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
        aria-label="Cambiar foto de perfil"
        data-testid="mp-header-avatar-btn"
        title="Haz clic para subir tu foto"
      >
        {perfilGlobal.foto_url ? (
          <AuthedImage
            url={perfilGlobal.foto_url}
            alt={perfilGlobal.nombre_completo}
            className="w-full h-full object-cover"
            testid="mp-header-avatar"
            fallback={
              <div className="w-full h-full bg-gradient-to-br from-brand-blue to-brand-navy text-white text-2xl font-bold flex items-center justify-center">
                {initials}
              </div>
            }
          />
        ) : (
          <div
            className="w-full h-full bg-gradient-to-br from-brand-blue to-brand-navy text-white text-2xl font-bold flex items-center justify-center shadow-inner"
            data-testid="mp-header-avatar"
          >
            {initials}
          </div>
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity text-white">
          {uploading ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={onPick}
        data-testid="mp-header-avatar-input"
      />
      {error && (
        <p className="mt-1 text-[11px] text-rose-600 max-w-[120px]" data-testid="mp-header-avatar-error">{error}</p>
      )}
    </div>
  );
}

function ProfileHeader() {
  const navigate = useNavigate();
  const { perfilGlobal, progreso, loading } = useProfile();

  if (loading || !perfilGlobal) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6" data-testid="mp-header-loading">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Skeleton variant="avatar" />
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton variant="title" />
            <Skeleton variant="text" />
          </div>
          <Skeleton variant="ring" />
        </div>
      </div>
    );
  }

  const initials = (perfilGlobal.nombre_completo || "CL")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const ubic = perfilGlobal.ubicacion;
  const ubicTxt = ubic
    ? [ubic.region, ubic.distrito].filter(Boolean).join(", ")
    : "Ubicación no definida";

  const siguiente = progreso?.siguiente_paso;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6" data-testid="mp-header">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
        {/* Avatar + identidad (nombre/tipo/ubicación siempre visibles) */}
        <div className="flex items-start gap-4 w-full md:w-auto md:flex-1">
          <AvatarUploader perfilGlobal={perfilGlobal} initials={initials} />
          <div className="min-w-0 flex-1">
            <h1 className="text-lg md:text-2xl font-bold text-slate-900 truncate" data-testid="mp-header-name">
              {perfilGlobal.nombre_completo}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              <span className="capitalize font-semibold text-slate-700">{perfilGlobal.tipo_usuario}</span>
              <span className="hidden md:inline"> · </span>
              <span className="block md:inline text-slate-500" data-testid="mp-header-ubic">{ubicTxt}</span>
            </p>
            <button
              onClick={() => navigate("/mi-perfil/publico")}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue hover:text-brand-navy"
              data-testid="mp-header-ver-publico"
            >
              <ExternalLink size={14} /> Ver mi Perfil Público
            </button>
          </div>
        </div>

        {/* Progreso */}
        <div className="flex items-center gap-4 md:pl-6 md:border-l md:border-slate-100 w-full md:w-auto">
          <ProfileProgressRing value={progreso?.porcentaje || 0} testid="mp-header-progress" />
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Perfil</p>
            <p className="text-sm text-slate-700 font-medium mt-0.5">
              {(progreso?.porcentaje || 0) < 100 ? "Sigue completando" : "¡Perfil completo!"}
            </p>
            {siguiente && (
              <button
                onClick={() => navigate(siguiente.ruta)}
                className="mt-1.5 text-xs font-semibold text-brand-blue hover:text-brand-navy text-left"
                data-testid="mp-header-next-step"
              >
                → {siguiente.etiqueta}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SubNav({ secciones }) {
  return (
    <nav className="lg:sticky lg:top-24 bg-white rounded-2xl border border-slate-100 p-2" data-testid="mp-subnav">
      <p className="hidden lg:block px-3 pt-2 pb-3 text-[11px] uppercase tracking-widest font-bold text-slate-400">
        Mi Perfil
      </p>
      <ul className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-1 lg:mx-0 px-1 lg:px-0">
        {secciones.map(({ to, label, icon: Icon, testid }) => (
          <li key={to} className="flex-shrink-0 lg:flex-shrink lg:w-full">
            <NavLink
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                [
                  "flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap lg:whitespace-normal",
                  isActive
                    ? "bg-brand-blue/10 text-brand-navy border border-brand-blue/20"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent",
                ].join(" ")
              }
            >
              <Icon size={16} />
              <span>{label}</span>
            </NavLink>
          </li>
        ))}
      </ul>

      {/* Acceso independiente al Marketplace (separado de las secciones de perfil) */}
      <div className="mt-2 pt-2 lg:border-t lg:border-slate-100">
        <Link
          to="/marketplace/crear-negocio"
          data-testid="mp-nav-crear-negocio"
          className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-bold whitespace-nowrap lg:whitespace-normal border transition-colors"
          style={{ backgroundColor: "rgba(217,119,6,0.10)", color: "#92400E", borderColor: "rgba(217,119,6,0.30)" }}
        >
          <Store size={16} />
          <span>Crear mi negocio</span>
        </Link>
        <p className="hidden lg:block px-3 pt-1.5 text-[10px] text-slate-400 leading-snug">
          Abre tu tienda en ConstruLink Marketplace
        </p>
      </div>
    </nav>
  );
}

function MiPerfilInner() {
  const location = useLocation();
  const { perfilGlobal } = useProfile();
  const secciones = seccionesVisibles(perfilGlobal?.tipo_usuario);
  const activa = SECCIONES.find((s) => location.pathname.startsWith(s.to));

  const crumbs = [
    { label: "Inicio", to: "/" },
    { label: "Mi Perfil", to: "/mi-perfil/global" },
    { label: activa ? activa.label : "Perfil Global" },
  ];

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6">
      <Breadcrumb items={crumbs} testid="mp-breadcrumb" />
      <ProfileHeader />
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside>
          <SubNav secciones={secciones} />
        </aside>
        <main data-testid="mp-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function MiPerfilLayout() {
  return (
    <ProfileProvider>
      <MiPerfilInner />
    </ProfileProvider>
  );
}
